import { create } from 'zustand';
import { connection } from '@/lib/api';
import { API_BASE } from '@/lib/config';
import { getToken } from '@/stores/authStore';

export interface ZoomConnection {
  id: string;
  accountId: string;
  accountName: string;
  scopes: string[];
  connectedAt: string;
  lastUsedAt?: string;
  credentialPurpose?: string;
}

export interface EnrichedAccount {
  id: string;
  accountId: string;
  accountName: string;
  authType: string;
  scopes: string[];
  connectedAt: string;
  lastUsedAt?: string;
  email?: string;
  displayName?: string;
  picUrl?: string;
  userType?: number;
  userTypeName?: string;
  roleName?: string;
  planUnitedType?: string;
  timezone?: string;
  company?: string;
  enrichmentStatus: 'ok' | 'token_expired' | 'error';
  enrichmentError?: string;
}

export interface ConnectionCapabilities {
  canRead: boolean;
  canWrite: boolean;
  readCredentialId?: string;
  writeCredentialId?: string;
  accountNumber?: string;
  organizationName?: string;
}

interface ConnectionState {
  // Multi-account state
  connections: ZoomConnection[];
  activeAccountId: string | null;
  isConnected: boolean;
  isLoading: boolean;
  isValidating: boolean;
  error: string | null;

  // Enriched accounts
  enrichedAccounts: EnrichedAccount[];
  isLoadingEnriched: boolean;

  // Capabilities
  capabilities: ConnectionCapabilities | null;

  // Actions
  checkConnection: () => Promise<void>;
  fetchEnrichedAccounts: () => Promise<void>;
  fetchCapabilities: () => Promise<ConnectionCapabilities | null>;
  /** Add write scopes to the Zoom app: the core set, or the ones a plan needs (D7). */
  /** `planId`: the plan the scopes are for, so its revert can take them back off the app. */
  /** `missing`: the scopes asked for that Zoom's new token still lacks (the app could not be given them). */
  enableWrite: (scopes?: string[], planId?: string) => Promise<{ enabled: boolean; error?: string; message?: string; missing?: string[] }>;
  /** Takes the scopes ZAC added for a plan back off the Zoom app (connection/s2s-remove-scopes.ts). */
  removeAddedScopes: (planId: string) => Promise<{ removed: string[]; kept: string[]; failed: string[]; error?: string; message?: string }>;
  disableWrite: () => Promise<boolean>;
  disconnect: (accountId?: string) => Promise<void>;
  setActiveAccount: (accountId: string) => void;
  getActiveConnection: () => ZoomConnection | null;
  getZoomConnectUrl: (returnTo?: string, purpose?: 'reader' | 'writer') => Promise<string | null>;
  startSidecarConnect: (purpose?: 'reader' | 'writer') => Promise<boolean>;
  clearError: () => void;
}

export const useConnectionStore = create<ConnectionState>((set, get) => ({
  connections: [],
  activeAccountId: null,
  isConnected: false,
  isLoading: false,
  isValidating: false,
  error: null,
  enrichedAccounts: [],
  isLoadingEnriched: false,
  capabilities: null,

  fetchEnrichedAccounts: async () => {
    set({ isLoadingEnriched: true });
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/accounts/enriched`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      const data = await res.json() as { accounts: EnrichedAccount[] };
      set({ enrichedAccounts: data.accounts });
    } catch {
      set({ enrichedAccounts: [] });
    } finally {
      set({ isLoadingEnriched: false });
    }
  },

  fetchCapabilities: async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/capabilities`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!res.ok) return null;
      const data = await res.json() as ConnectionCapabilities;
      set({ capabilities: data });
      return data;
    } catch {
      return null;
    }
  },

  enableWrite: async (scopes, planId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/s2s/enable-write`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ ...(scopes && scopes.length > 0 ? { scopes } : {}), ...(planId ? { plan_id: planId } : {}) }),
      });
      const data = (await res.json().catch(() => ({}))) as { enabled?: boolean; error?: string; message?: string; missing?: string[] };
      if (data.enabled) await get().fetchCapabilities();
      return { enabled: !!data.enabled, error: data.error, message: data.message, ...(data.missing && data.missing.length > 0 ? { missing: data.missing } : {}) };
    } catch {
      return { enabled: false, error: 'network_error', message: 'Network error — try again.' };
    }
  },

  removeAddedScopes: async (planId) => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/s2s/remove-scopes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ plan_id: planId }),
      });
      const data = (await res.json().catch(() => ({}))) as { removed?: string[]; kept?: string[]; failed?: string[]; error?: string; message?: string };
      if ((data.removed ?? []).length > 0) await get().fetchCapabilities();
      return { removed: data.removed ?? [], kept: data.kept ?? [], failed: data.failed ?? [], error: data.error, message: data.message };
    } catch {
      return { removed: [], kept: [], failed: [], error: 'network_error', message: 'Network error — try again.' };
    }
  },

  disableWrite: async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/connection/s2s/disable-write`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({}),
      });
      const data = (await res.json().catch(() => ({}))) as { disabled?: boolean };
      if (data.disabled) await get().fetchCapabilities();
      return !!data.disabled;
    } catch {
      return false;
    }
  },

  checkConnection: async () => {
    set({ isLoading: true, error: null });
    try {
      // Get all connected accounts
      const listResponse = await connection.listAccounts();
      const connections = listResponse.connections as ZoomConnection[];

      // Get active connection status
      const statusResponse = await connection.getStatus();
      const activeAccountId = statusResponse.connection?.accountId ?? null;

      set({
        connections,
        activeAccountId,
        isConnected: connections.length > 0,
      });
    } catch {
      // Not connected is not an error
      set({ isConnected: false, connections: [], activeAccountId: null });
    } finally {
      set({ isLoading: false });
    }
  },



  disconnect: async (accountId?: string) => {
    const { connections, activeAccountId } = get();
    const targetAccountId = accountId ?? activeAccountId;

    if (!targetAccountId) {
      set({ error: 'No account to disconnect' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      await connection.disconnectAccount(targetAccountId);

      const updatedConnections = connections.filter(c => c.accountId !== targetAccountId);

      // If we disconnected the active account, select the next one by lastUsedAt
      let newActiveAccountId = activeAccountId;
      if (targetAccountId === activeAccountId) {
        if (updatedConnections.length > 0) {
          // Sort by lastUsedAt (most recent first) and pick the first
          const sorted = [...updatedConnections].sort((a, b) => {
            const aTime = a.lastUsedAt ? new Date(a.lastUsedAt).getTime() : 0;
            const bTime = b.lastUsedAt ? new Date(b.lastUsedAt).getTime() : 0;
            return bTime - aTime;
          });
          newActiveAccountId = sorted[0]?.accountId ?? null;
        } else {
          newActiveAccountId = null;
        }
      }

      set({
        connections: updatedConnections,
        activeAccountId: newActiveAccountId,
        isConnected: updatedConnections.length > 0,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Disconnection failed';
      set({ error: message });
    } finally {
      set({ isLoading: false });
    }
  },

  setActiveAccount: (accountId: string) => {
    const { connections } = get();
    const exists = connections.some(c => c.accountId === accountId);
    if (exists) {
      set({ activeAccountId: accountId });
    }
  },

  getActiveConnection: () => {
    const { connections, activeAccountId } = get();
    if (!activeAccountId) return null;
    return connections.find(c => c.accountId === activeAccountId) ?? null;
  },

  getZoomConnectUrl: async (returnTo?: string, purpose?: 'reader' | 'writer') => {
    try {
      const token = getToken();
      const params = new URLSearchParams();
      if (returnTo) params.set('returnTo', returnTo);
      if (purpose) params.set('purpose', purpose);
      const qs = params.toString();
      const resp = await fetch(`${API_BASE}/connection/zoom/url${qs ? `?${qs}` : ''}`, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
        },
      });
      if (!resp.ok) {
        throw new Error('Failed to get Zoom connect URL');
      }
      const data = await resp.json() as { url: string };
      return data.url;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to get connect URL';
      set({ error: message });
      return null;
    }
  },

  startSidecarConnect: async (purpose?: 'reader' | 'writer') => {
    try {
      const token = getToken();
      const resp = await fetch(`${API_BASE}/connection/zoom/start-sidecar`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'X-Tenant-ID': '',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ purpose: purpose ?? 'writer' }),
      });
      if (!resp.ok) {
        throw new Error('Failed to start sidecar Zoom connect flow');
      }
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start sidecar connect';
      set({ error: message });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));
